import React, { useState } from "react";
import {
  styled,
  Typography,
  SxProps,
  Stack,
  Box,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Divider,
  Autocomplete,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { OrderCheckoutRequest } from "../../../Core/Api/Types/order-checkout-request";
import { produce } from "immer";
import { DbOrderBenefitTypeInfos } from "../../../Core/Api/Types/db-order-benefit-type-infos";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { cacheService } from "../../../Core/Api/services";
import { useTranslation } from "react-i18next";

export type ConfigOrderOptionsFormProps = {
  className?: string;
  sx?: SxProps;
  children?: React.ReactNode;
  checkout: OrderCheckoutRequest;
  costCenters?: DbOrderBenefitTypeInfos[];
  setCheckout: (value: React.SetStateAction<OrderCheckoutRequest>) => void;
};

const ConfigOrderOptionsForm: React.FC<ConfigOrderOptionsFormProps> = (props) => {
  const { t } = useTranslation();
  const { children, checkout, setCheckout, costCenters = null, ...otherProps } = props;
  const [costCenterSelected, setCostCenterSelected] = useState<DbOrderBenefitTypeInfos>();

  return (
    <Box width="100%" {...otherProps}>
      <Accordion expanded>
        <AccordionSummary
          expandIcon={
            <>
              <AddIcon sx={{ color: "white" }} />
              <RemoveIcon sx={{ color: "white" }} />
            </>
          }
          aria-controls="config-content"
          id="config-header"
        >
          <Typography variant="subtitle2" component="h3" color="text.primary">
            {t("component:body.orderOptions")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Form>
            <Stack mr={5} flex={1}>
              <Typography variant="h6" mb={2.5}>
                {t("common:order")}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={checkout.dueDate}
                  label={t("field:label.selectADate")}
                  format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                  onChange={(v, keyboard) => {
                    setCheckout(
                      produce((draft) => {
                        draft.dueDate = v;
                      })
                    );
                  }}
                  /*renderInput={(params) => {
                    const outlinedInputProps = {
                      ...params.InputProps,
                      value: params.inputProps?.value || "Select a date",
                      type: "text",
                      readOnly: params.inputProps?.readOnly,
                      placeholder: params.inputProps?.placeholder || "Select a date",
                    };
                    return (
                      <FormControl sx={{ mb: 3 }} ref={params.inputRef}>
                        <InputLabel>Requested delivery date</InputLabel>
                        <OutlinedInput {...outlinedInputProps} />
                      </FormControl>
                    );
                  }}*/
                />
              </LocalizationProvider>
              <FormControl sx={{ mt: 3, mb: 3 }}>
                <InputLabel shrink htmlFor="external-reference">
                  {t("field:label.externalReference")}
                </InputLabel>
                <OutlinedInput
                  defaultValue=""
                  id="external-reference"
                  value={checkout.externalReference}
                  onChange={(e) => {
                    setCheckout(
                      produce((draft) => {
                        draft.externalReference = e.target.value;
                      })
                    );
                  }}
                />
              </FormControl>
              <FormControl sx={{ mb: 3 }}>
                <FormControlLabel
                  variant="outlined"
                  control={<Checkbox defaultChecked />}
                  checked={checkout.isImportant}
                  onChange={(e, c) => {
                    setCheckout(
                      produce((draft) => {
                        draft.isImportant = !draft.isImportant;
                      })
                    );
                  }}
                  label={
                    <Stack>
                      <Typography fontWeight={500}>{t("common:majorDeliveryLowerCase")}</Typography>
                      <Typography fontWeight={500} variant="body2" color="text.tier">
                        {cacheService.GetAppData()?.platformId !== 1 && t("field:label.isCritialCheckbox")}
                      </Typography>
                    </Stack>
                  }
                />
              </FormControl>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" mb={2.5}>
                {t("common:billing")}
              </Typography>
              {costCenters && costCenters.length > 0 && (
                <FormControl sx={{ mb: 3 }}>
                  <InputLabel shrink htmlFor="external-reference">
                    {t("field:label.costBenefitCenter")}
                  </InputLabel>
                  <Autocomplete
                    options={costCenters}
                    value={costCenterSelected}
                    onChange={(e, v) => {
                      if (!v) return;
                      setCostCenterSelected(v);
                      setCheckout(
                        produce((draft) => {
                          draft.costBenefitsLinkId = v.id;
                        })
                      );
                    }}
                    onReset={(v) => {
                      // Non executed... strange, must use onChange
                      setCheckout(
                        produce((draft) => {
                          draft.costBenefitsLinkId = null;
                        })
                      );
                    }}
                    getOptionLabel={(value: any) => value.type + " - " + value.description}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(x, s) =>
                      x.filter(
                        (a) =>
                          a?.type?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                          a?.description?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                      )
                    }
                    renderInput={(params) => (
                      <OutlinedInput
                        {...params.InputProps}
                        inputProps={params.inputProps}
                        placeholder={t("field:placeholder.selectCostBenefitCenter")}
                        required
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                  <FormHelperText>{t("field:helper.costBenefitCenter")}</FormHelperText>
                </FormControl>
              )}
            </Stack>
            <Stack ml={5} flex={1}>
              <Typography variant="h6" mb={2.5}>
                {t("component:body.additionalComments")}
              </Typography>
              <FormControl sx={{ mb: 3 }}>
                <InputLabel shrink htmlFor="comments">
                  {t("field:label.comments")}
                </InputLabel>
                <OutlinedInput
                  defaultValue=""
                  id="comments"
                  placeholder={t("field:placeholder.optionalComment")}
                  multiline
                  rows={6}
                  value={checkout.comment}
                  onChange={(e) => {
                    setCheckout(
                      produce((draft) => {
                        draft.comment = e.target.value;
                      })
                    );
                  }}
                />
              </FormControl>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="h6" mb={2.5}>
                {t("component:body.additionalOptions")}
              </Typography>
              {UserHasAcl(AclTypes.aclOrderPlayWithoutApproval) && (
                <FormControl sx={{ mb: 3 }}>
                  <FormControlLabel
                    variant="outlined"
                    control={<Checkbox defaultChecked />}
                    checked={checkout.autoPlay}
                    onChange={(e, c) => {
                      setCheckout(
                        produce((draft) => {
                          draft.autoPlay = !draft.autoPlay;
                        })
                      );
                    }}
                    label={
                      <Stack>
                        <Typography fontWeight={500}>{t("field:label.playOrdersAuto")}</Typography>
                        <Typography fontWeight={500} variant="body2" color="text.tier">
                          {t("field:label.autoPlayCheckbox")}
                        </Typography>
                      </Stack>
                    }
                  />
                </FormControl>
              )}
              <FormControl sx={{ mb: 3 }}>
                <FormControlLabel
                  variant="outlined"
                  control={<Checkbox defaultChecked />}
                  checked={checkout.clearBasket}
                  onChange={(e, c) => {
                    setCheckout(
                      produce((draft) => {
                        draft.clearBasket = !draft.clearBasket;
                      })
                    );
                  }}
                  label={
                    <Stack>
                      <Typography fontWeight={500}>{t("field:label.clearMyBasket")}</Typography>
                      <Typography fontWeight={500} variant="body2" color="text.tier">
                        {t("field:label.clearBasketCheckbox")}
                      </Typography>
                    </Stack>
                  }
                />
              </FormControl>
              <FormControl sx={{ mb: 3 }}>
                <FormControlLabel
                  variant="outlined"
                  control={<Checkbox defaultChecked />}
                  checked={checkout.clearBasketOptions}
                  onChange={(e, c) => {
                    setCheckout(
                      produce((draft) => {
                        draft.clearBasketOptions = !draft.clearBasketOptions;
                      })
                    );
                  }}
                  label={
                    <Stack>
                      <Typography fontWeight={500}>{t("field:label.clearBasketConfig")}</Typography>
                      <Typography fontWeight={500} variant="body2" color="text.tier">
                        {t("field:label.clearBasketOptionsCheckbox")}
                      </Typography>
                    </Stack>
                  }
                />
              </FormControl>
              {cacheService.GetPlatformSettings()?.cloud && (
                <FormControl sx={{ mb: 3 }}>
                  <FormControlLabel
                    variant="outlined"
                    control={<Checkbox defaultChecked />}
                    checked={checkout.restoreExpedit}
                    onChange={(e, c) => {
                      setCheckout(
                        produce((draft) => {
                          draft.restoreExpedit = !draft.restoreExpedit;
                        })
                      );
                    }}
                    label={
                      <Stack>
                        <Typography fontWeight={500}>{t("field:label.instantRestore")}</Typography>
                        <Typography fontWeight={500} variant="body2" color="text.tier">
                          {t("field:label.immediateAccessFromAwsArchive")}
                        </Typography>
                      </Stack>
                    }
                  />
                </FormControl>
              )}
              <FormControl sx={{ mb: UserHasAcl(AclTypes.aclSuperUser) ? 3 : 2 }}>
                <FormControlLabel
                  variant="outlined"
                  control={<Checkbox defaultChecked />}
                  checked={checkout.followUpEmail}
                  onChange={(e, c) => {
                    setCheckout(
                      produce((draft) => {
                        draft.followUpEmail = !draft.followUpEmail;
                      })
                    );
                  }}
                  label={
                    <Stack>
                      <Typography fontWeight={500}>{t("field:label.trackingWithFollowupEmail")}</Typography>
                      <Typography fontWeight={500} variant="body2" color="text.tier">
                        {t("field:label.followUpEmailCheckbox")}
                      </Typography>
                    </Stack>
                  }
                />
              </FormControl>
              {UserHasAcl(AclTypes.aclSuperUser) && (
                <FormControl sx={{ mb: 2 }}>
                  <FormControlLabel
                    variant="outlined"
                    control={<Checkbox />}
                    checked={checkout.hide}
                    onChange={(e, c) => {
                      setCheckout(
                        produce((draft) => {
                          draft.hide = !draft.hide;
                        })
                      );
                    }}
                    label={
                      <Stack>
                        <Typography fontWeight={500}>{t("field:label.hideItemsInBasket")}</Typography>
                        <Typography fontWeight={500} variant="body2" color="text.tier">
                          {t("field:label.hideItemsCheckbox")}
                        </Typography>
                      </Stack>
                    }
                  />
                </FormControl>
              )}
            </Stack>
          </Form>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: theme.palette.mode === "dark" ? theme.palette.backgroundAlpha.main : theme.palette.background.paper,
  ".MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
    "&:first-of-type": {
      display: "block",
    },
    "&:last-of-type": {
      display: "none",
    },
  },
  "&.Mui-expanded": {
    ".MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
      "&:first-of-type": {
        display: "none",
      },
      "&:last-of-type": {
        display: "block",
      },
    },
  },
}));

const Form = styled("form")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
}));

export default ConfigOrderOptionsForm;
